import * as React from "react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

/**
 * ButtonWithTooltip - A wrapper that adds tooltip with long-press support for mobile
 * Uses the same pattern as TabWithTooltip but for plain buttons
 */
const ButtonWithTooltip = React.forwardRef(({
  title,
  description,
  children,
  className,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false);
  const longPressTimeout = React.useRef(null);
  const isTouchRef = React.useRef(false);

  const clearLongPress = () => {
    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout.current);
      longPressTimeout.current = null;
    }
  };

  const handlePointerDown = (e) => {
    if (e.pointerType === 'touch') {
      isTouchRef.current = true;
      clearLongPress();
      longPressTimeout.current = setTimeout(() => setOpen(true), 500);
    }
  };

  const handlePointerUp = () => {
    clearLongPress();
    if (isTouchRef.current && open) {
      setTimeout(() => setOpen(false), 2000);
    }
    isTouchRef.current = false;
  };

  const handlePointerMove = () => {
    if (isTouchRef.current) clearLongPress();
  };

  React.useEffect(() => () => clearLongPress(), []);

  const ariaLabel = description ? `${title}: ${description}` : title;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip
        open={open}
        onOpenChange={(newOpen) => {
          if (!isTouchRef.current) setOpen(newOpen);
        }}
      >
        <TooltipTrigger asChild>
          <span
            ref={ref}
            className={cn("inline-block", className)}
            aria-label={ariaLabel}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerMove={handlePointerMove}
            onPointerCancel={handlePointerUp}
            {...props}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-[250px] text-center"
          style={{ backgroundColor: '#163329', color: '#F5F5F0' }}
        >
          <p className="font-semibold text-sm">{title}</p>
          {description && (
            <p className="text-xs mt-1 opacity-90">{description}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

ButtonWithTooltip.displayName = "ButtonWithTooltip";
export { ButtonWithTooltip }
